/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useRef, useState, useEffect, useCallback } from "react";
import "./App.scss";
import { LiveAPIProvider } from "./contexts/LiveAPIContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Altair } from "./components/altair/Altair";
import ControlTray, { ControlTrayHandle } from "./components/control-tray/ControlTray";
import { useLiveAPIContext } from "./contexts/LiveAPIContext";
import SplashScreen from "./components/splash-screen/SplashScreen";
import LoginScreen from "./components/login/LoginScreen";
import WelcomeMessage from "./components/welcome-message/WelcomeMessage";
import { AllPermissions } from "./utils/permissions";
import cn from "classnames";
import { LiveClientOptions } from "./types";
import { isAuthenticated, getUserData, clearSession } from "./utils/session";

const API_KEY = "AIzaSyCc0KGMh0lBOorfrxY74UR44_FyAldKliE" as string;

if (!API_KEY || typeof API_KEY !== "string") {
  console.error("REACT_APP_GEMINI_API_KEY is required but not found. Please set your API key in .env file.");
  throw new Error("Missing required environment variable: REACT_APP_GEMINI_API_KEY");
}

console.log("🔑 [GEMINI] API Key loaded for Gemini Live API");
const apiOptions: LiveClientOptions = {
  apiKey: API_KEY,
};
console.log("⚙️ [GEMINI] LiveAPI options configured:", { apiKey: API_KEY.substring(0, 10) + "..." });

// Main app component that handles the LiveAPI session
function MainApp({ 
  userData, 
  permissions,
  voiceGender,
  onLogout 
}: { 
  userData: {username: string; mobileNumber: string} | null;
  permissions: AllPermissions | null;
  voiceGender: 'male' | 'female';
  onLogout: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
  const { disconnect, stopAudio } = useLiveAPIContext();
  
  // Camera controls for vehicle survey
  const [cameraControls, setCameraControls] = useState<ControlTrayHandle | null>(null);

  const handleLogout = async () => {
    // Stop audio streaming immediately (first priority)
    stopAudio();
    
    // Stop any active video streams
    if (videoStream) {
      videoStream.getTracks().forEach(track => track.stop());
      setVideoStream(null);
    }
    
    // Clear video element
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    
    // Disconnect the LiveAPI session
    await disconnect();
    
    // Then call the parent logout handler
    onLogout();
  };

  // Camera control callbacks for Altair (vehicle survey)
  const handleCameraControlsReady = useCallback((controls: ControlTrayHandle) => {
    console.log("📹 [App] Camera controls received from ControlTray");
    setCameraControls(controls);
  }, []);

  const handleRequestStartCamera = useCallback(async (): Promise<boolean> => {
    if (cameraControls) {
      return await cameraControls.startCamera();
    }
    console.warn("⚠️ [App] Camera controls not available");
    return false;
  }, [cameraControls]);

  const handleRequestCaptureFrame = useCallback(async (): Promise<string | null> => {
    if (cameraControls) {
      return await cameraControls.captureFrame();
    }
    console.warn("⚠️ [App] Camera controls not available");
    return null;
  }, [cameraControls]);

  const handleRequestStopCamera = useCallback(() => {
    if (cameraControls) {
      cameraControls.stopCamera();
    }
  }, [cameraControls]);

  return (
    <div 
      className="streaming-console"
      style={{
        // Set gender splash as background during connection
        backgroundImage: voiceGender === 'male' 
          ? 'url(/man-splash.jpeg)' 
          : 'url(/woman-splash.jpeg)',
        backgroundSize: 'cover', // Changed from '100% 100%' to 'cover' to maintain aspect ratio
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <main>
        <div className="main-app-area">
          {/* APP goes here */}
          <Altair 
            key={userData?.username || 'no-user'} 
            permissions={permissions} 
            userData={userData} 
            voiceGender={voiceGender}
            onRequestStartCamera={handleRequestStartCamera}
            onRequestCaptureFrame={handleRequestCaptureFrame}
            onRequestStopCamera={handleRequestStopCamera}
          />
          <video
            className={cn("stream", {
              hidden: !videoRef.current || !videoStream,
            })}
            ref={videoRef}
            autoPlay
            playsInline
          />
        </div>

        <ControlTray
          videoRef={videoRef}
          supportsVideo={true}
          onVideoStreamChange={setVideoStream}
          onCameraControlsReady={handleCameraControlsReady}
        >
          {/* Logout button */}
          {userData && (
            <button
              className="action-button logout-button"
              onClick={handleLogout}
              title={`Logout (${userData.username})`}
            >
              <span className="material-symbols-outlined">logout</span>
            </button>
          )}
        </ControlTray>
      </main>
    </div>
  );
}

function App() {
  // Splash screen state
  const [showSplash, setShowSplash] = useState<boolean>(true);
  // Login screen state
  const [showLogin, setShowLogin] = useState<boolean>(false);
  // Welcome message state
  const [showWelcome, setShowWelcome] = useState<boolean>(false);
  // Gender-specific splash state (shown after voice selection)
  const [showGenderSplash, setShowGenderSplash] = useState<boolean>(false);
  // Permissions state
  const [permissions, setPermissions] = useState<AllPermissions | null>(null);
  // User data state
  const [userData, setUserData] = useState<{username: string; mobileNumber: string} | null>(null);
  // Voice gender preference
  const [voiceGender, setVoiceGender] = useState<'male' | 'female'>('male');

  useEffect(() => {
    // Check if user is already authenticated via session
    if (isAuthenticated()) {
      const userDataFromSession = getUserData();
      if (userDataFromSession) {
        setUserData(userDataFromSession);
      } else {
        // Session exists but user data is invalid, clear it
        clearSession();
      }
    }
  }, []);

  // Handle splash screen completion
  const handleSplashComplete = () => {
    setShowSplash(false);
    // Always check authentication status - login is mandatory
    if (isAuthenticated() && userData) {
      setShowWelcome(true);
    } else {
      // User must login - clear any invalid session data
      clearSession();
      setUserData(null);
      setShowLogin(true);
    }
  };

  // Handle login completion
  const handleLoginComplete = (loginData: {username: string; mobileNumber: string}) => {
    setUserData(loginData);
    setShowLogin(false);
    setShowWelcome(true);
  };

  // Handle welcome message completion with permissions
  const handleWelcomeComplete = (grantedPermissions: AllPermissions, selectedVoiceGender: 'male' | 'female') => {
    console.log(`📋 [APP] Welcome complete - Selected voice: ${selectedVoiceGender}`);
    setPermissions(grantedPermissions);
    setVoiceGender(selectedVoiceGender);
    console.log(`🎤 [APP] Voice gender selected: ${selectedVoiceGender}`);
    setShowWelcome(false);
    console.log(`🖼️ [APP] Setting showGenderSplash to TRUE`);
    // Show gender-specific splash screen
    setShowGenderSplash(true);
  };
  
  // Handle gender splash completion
  const handleGenderSplashComplete = () => {
    console.log(`✅ [APP] Gender splash complete - hiding gender splash`);
    setShowGenderSplash(false);
  };

  // Handle logout
  const handleLogout = () => {
    clearSession();
    setUserData(null);
    setPermissions(null);
    setShowLogin(true);
  };

  return (
    <ThemeProvider>
      <div className="App" style={{ 
        // Prevent white flash between splashes
        backgroundColor: '#000000',
        minHeight: '100vh'
      }}>
        {(() => {
          console.log(`🎭 [APP] Render state:`, {
            showSplash,
            showLogin,
            showWelcome,
            showGenderSplash,
            voiceGender,
            shouldShowMainApp: !showSplash && !showLogin && !showWelcome && !showGenderSplash
          });
          return null;
        })()}
        
        {/* Initial Splash Screen (default splash.png) */}
        {showSplash && (
          <SplashScreen key="default-splash" onComplete={handleSplashComplete} gender={null} />
        )}
        
        {/* Login Screen */}
        {showLogin && (
          <LoginScreen onComplete={handleLoginComplete} />
        )}
        
        {/* Welcome Message */}
        {showWelcome && (
          <WelcomeMessage onComplete={handleWelcomeComplete} />
        )}
        
        {/* Gender-Specific Splash Screen (splash_male.png or splash_female.png) */}
        {showGenderSplash && (
          <SplashScreen key={`gender-splash-${voiceGender}`} onComplete={handleGenderSplashComplete} gender={voiceGender} />
        )}
        
        {/* Only show LiveAPIProvider and MainApp after all splashes and welcome are complete */}
        {!showSplash && !showLogin && !showWelcome && !showGenderSplash && (
          <LiveAPIProvider key={userData?.username || 'no-user'} options={apiOptions}>
            {(() => {
              console.log("🚀 [GEMINI] LiveAPIProvider initialized with user:", userData?.username || 'no-user');
              return null;
            })()}
            <MainApp 
              userData={userData}
              permissions={permissions}
              voiceGender={voiceGender}
              onLogout={handleLogout}
            />
          </LiveAPIProvider>
        )}
      </div>
    </ThemeProvider>
  );
}

export default App;
